// src/components/Dashboard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { updateFavicon, updatePageTitle, removeFavicon } from '../utils/faviconUtils';
import TenantWebsite from './TenantWebsite/index.js';
import DefaultTemplate from './DefaultTemplate';
import Lottie from 'lottie-react';
import bookLoadingAnimation from '../animations/Book loading.json';
import DisabledPage from '../DisabledPage';
import SessionConsentModal from '../components/SessionConsentModal';
import OTPModal from '../components/OTPModal';

// ✅ Helper to get or create device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    // Generate a unique device ID using crypto.randomUUID() or fallback
    try {
      deviceId = 'dev_' + crypto.randomUUID();
    } catch (e) {
      deviceId = 'dev_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    }
    localStorage.setItem('deviceId', deviceId);
    console.log('🆕 New device ID generated:', deviceId);
  }
  return deviceId;
};

const Dashboard = ({ subdomain, onTenantBrandingUpdate }) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [websiteData, setWebsiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setUserRole] = useState(null);
  const [, setUserId] = useState(null);
  const [, setTenantId] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [, setLoginAttempts] = useState(0);
  
  // Consent modal states
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [consentLoading, setConsentLoading] = useState(false);
  
  // OTP Modal states
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpRemainingAttempts, setOtpRemainingAttempts] = useState(3);
  const [otpTimerSeconds, setOtpTimerSeconds] = useState(60);
  const [otpLoginData, setOtpLoginData] = useState(null);
  
  const navigate = useNavigate();
  const didRunRef = useRef(false);
  const loginInProgressRef = useRef(false);

  // Helper function to resolve relative URLs
  const resolveUrl = useCallback((src) => {
    if (!src) return '';
    const s = String(src).trim();
    
    if (/^https?:\/\//i.test(s) || s.startsWith('//')) return s;
    if (s.startsWith('data:')) return s;
    
    const base = (api && api.defaults && api.defaults.baseURL) 
      ? api.defaults.baseURL.replace(/\/$/, '') 
      : '';
    
    if (s.startsWith('/')) {
      return base ? `${base}${s}` : s;
    }
    
    return base ? `${base}/${s.replace(/^\/*/, '')}` : s;
  }, []);

  const clearBrandingData = useCallback(() => {
    console.log('🧹 Clearing all branding data');
    localStorage.removeItem('tenantFavicon');
    localStorage.removeItem('tenantTitle');
    localStorage.removeItem('tenantBranding');
    removeFavicon();
    document.title = 'Truyon';
    if (typeof onTenantBrandingUpdate === 'function') {
      try {
        onTenantBrandingUpdate({ favicon: null, title: null });
      } catch (e) {
        console.warn('Error notifying parent of branding clear:', e);
      }
    }
  }, [onTenantBrandingUpdate]);

  const applyBranding = useCallback((brandingData) => {
    if (!brandingData) {
      console.log('⚠️ No branding data to apply');
      clearBrandingData();
      return;
    }
    
    console.log('🎨 Applying branding data:', brandingData);
    
    let favicon = brandingData.favicon || brandingData.logo || brandingData.icon || '';
    const title = brandingData.title || brandingData.name || '';
    const resolvedFavicon = favicon ? resolveUrl(favicon) : '';
    
    console.log('📌 Resolved favicon:', resolvedFavicon);
    console.log('📌 Title:', title);
    
    if (resolvedFavicon && resolvedFavicon.trim() !== '') {
      const storedFavicon = localStorage.getItem('tenantFavicon');
      if (resolvedFavicon !== storedFavicon) {
        localStorage.setItem('tenantFavicon', resolvedFavicon);
        updateFavicon(resolvedFavicon);
        console.log('✅ Updated favicon to:', resolvedFavicon);
      }
    } else {
      if (localStorage.getItem('tenantFavicon')) {
        localStorage.removeItem('tenantFavicon');
        removeFavicon();
      }
    }
    
    if (title && title.trim() !== '') {
      const storedTitle = localStorage.getItem('tenantTitle');
      if (title !== storedTitle) {
        localStorage.setItem('tenantTitle', title);
        updatePageTitle(title);
        console.log('✅ Updated title to:', title);
      }
    } else {
      if (localStorage.getItem('tenantTitle')) {
        localStorage.removeItem('tenantTitle');
      }
      updatePageTitle('Truyon');
    }
    
    const brandingToStore = {
      favicon: resolvedFavicon,
      title: title || 'Truyon',
      timestamp: Date.now()
    };
    localStorage.setItem('tenantBranding', JSON.stringify(brandingToStore));
    
    if (typeof onTenantBrandingUpdate === 'function') {
      try {
        onTenantBrandingUpdate({ 
          favicon: resolvedFavicon, 
          title: title || 'Truyon' 
        });
      } catch (e) {
        console.warn('Error notifying parent of branding update:', e);
      }
    }
  }, [resolveUrl, clearBrandingData, onTenantBrandingUpdate]);

  useEffect(() => {
    try {
      const storedRole = localStorage.getItem('userRole');
      const storedUserId = localStorage.getItem('userId');
      const storedTenantId = localStorage.getItem('tenantId');
      
      if (storedRole) setUserRole(storedRole);
      if (storedUserId) setUserId(storedUserId);
      if (storedTenantId) setTenantId(storedTenantId);
      
      if (storedRole === 'faculty' && storedUserId && storedTenantId) {
        const isOnFacultyRoute = window.location.pathname.includes('/dashboard');
        if (!isOnFacultyRoute) {
          navigate('/dashboard', { 
            state: { userId: storedUserId, tenantId: storedTenantId } 
          });
        }
      }
      
      const storedBranding = localStorage.getItem('tenantBranding');
      if (storedBranding) {
        try {
          const branding = JSON.parse(storedBranding);
          const timestamp = branding.timestamp || 0;
          const oneDay = 24 * 60 * 60 * 1000;
          
          if (Date.now() - timestamp > oneDay) {
            console.log('🕐 Stale branding found, clearing...');
            clearBrandingData();
          }
        } catch (e) {
          console.warn('Error parsing stored branding:', e);
          clearBrandingData();
        }
      }
    } catch (e) {
      console.warn('Error restoring auth info:', e);
    }
  }, [navigate, clearBrandingData]);

  useEffect(() => {
    const fetchData = async () => {
      console.group('📡 Dashboard Data Fetching');
      console.log('🎯 Fetching data for subdomain:', subdomain);
      
      setLoading(true);
      
      try {
        const websiteResponse = await api.get(`/api/website/content/${subdomain}`);
        console.log('✅ Website Response:', websiteResponse.data);
        
        const { success, website } = websiteResponse.data;
        
        if (success && website) {
          console.log('🏠 Website found - applying branding');
          setStatus('active');
          setWebsiteData(website);
          
          if (website.branding) {
            applyBranding(website.branding);
          } else {
            console.log('⚠️ Website has no branding data');
            clearBrandingData();
          }
        } else {
          console.log('❌ No website data found, using default template');
          setStatus('active');
          setWebsiteData(null);
          clearBrandingData();
        }
      } catch (err) {
        console.group('❌ Error Details');
        console.error('Error:', err);
        console.error('Response:', err.response?.data);
        console.groupEnd();
        
        if (err.response?.status === 404) {
          const errorMessage = err.response.data?.message || err.response.data?.error;
          console.log('🔍 404 Error Message:', errorMessage);
          
          if (errorMessage?.includes('tenant') || errorMessage?.includes('Website not found')) {
            console.log('🏠 Tenant exists but no website - using default template');
            setStatus('active');
            setWebsiteData(null);
            clearBrandingData();
          } else {
            console.log('❌ Tenant not found or inactive');
            setStatus('not_found');
            clearBrandingData();
          }
        } else if (err.response?.status === 400) {
          console.log('❌ Bad request - subdomain required');
          setStatus('error');
          setError('Subdomain is required');
          clearBrandingData();
        } else {
          console.log('❌ Other error');
          setStatus('error');
          setError(err.response?.data?.message || 'Error fetching data. Please try again later.');
        }
      } finally {
        setLoading(false);
        console.groupEnd();
      }
    };
    
    if (didRunRef.current) return;
    didRunRef.current = true;
    fetchData();
  }, [subdomain, applyBranding, clearBrandingData]);

  // ============================================
  // OTP HANDLERS
  // ============================================

  const handleOTPVerify = async (otpCode) => {
    setOtpLoading(true);
    setOtpError(null);
    
    try {
      console.log('🔐 Verifying OTP...');
      
      const loginData = otpLoginData || {
        email: otpEmail || email,
        password: password,
        subdomain: window.location.hostname.split('.')[0]
      };
      
      // ✅ Get device ID
      const deviceId = getDeviceId();
      
      const loginWithOTP = {
        ...loginData,
        otp: otpCode,
        forceLogin: true,
        deviceId: deviceId
      };
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            });
          });
          
          loginWithOTP.latitude = position.coords.latitude;
          loginWithOTP.longitude = position.coords.longitude;
          console.log('✅ Location captured for OTP login');
        } catch (geoError) {
          console.log('⚠️ Location not available:', geoError.message);
        }
      }
      
      console.log('📱 Sending OTP with deviceId:', deviceId);
      const response = await api.post(`/api/new-auth/subdomain-signin`, loginWithOTP);
      
      await handleLoginSuccess(response.data);
      
      setShowOTPModal(false);
      setOtpLoading(false);
      
    } catch (err) {
      console.error('❌ OTP verification failed:', err);
      
      if (err.response?.data?.code === 'OTP_INVALID' || err.response?.data?.code === 'OTP_EXPIRED') {
        setOtpError(err.response?.data?.message || 'Invalid OTP. Please try again.');
        setOtpRemainingAttempts(err.response?.data?.remainingAttempts || 2);
        
        if (err.response?.data?.remainingAttempts === 0) {
          setTimeout(() => {
            setShowOTPModal(false);
            setLoginError('Too many failed OTP attempts. Please try logging in again.');
          }, 2000);
        }
      } else {
        setOtpError(err.response?.data?.message || 'Verification failed. Please try again.');
      }
      
      setOtpLoading(false);
    }
  };

  const handleOTPResend = async () => {
    setOtpError(null);
    
    try {
      console.log('📧 Resending OTP...');
      
      const loginData = otpLoginData || {
        email: otpEmail || email,
        subdomain: window.location.hostname.split('.')[0]
      };
      
      const response = await api.post(`/api/new-auth/resend-otp`, {
        email: loginData.email
      });
      
      if (response.data?.success) {
        console.log('✅ OTP resent successfully');
        setOtpTimerSeconds(60);
        return response.data;
      } else {
        throw new Error(response.data?.message || 'Failed to resend OTP');
      }
      
    } catch (err) {
      console.error('❌ Failed to resend OTP:', err);
      setOtpError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
      throw err;
    }
  };

  const handleOTPTimerComplete = () => {
    console.log('⏰ OTP timer expired');
  };

  // ============================================
  // LOGIN HANDLERS
  // ============================================

  const handleLoginSuccess = async (responseData) => {
    const userData = responseData.user || {};
    const userIdFromResponse = responseData.userId || userData.id || userData._id || userData.userId;
    const token = responseData.token;
    const role = userData.role || responseData.role;
    const tenantIdFromResponse = userData.tenantId || responseData.tenantId;
    const mustChangePassword = responseData.mustChangePassword || false;
    const passwordChangeReason = responseData.passwordChangeReason || '';
    const sessionId = responseData.sessionId;
    const tokenVersion = responseData.tokenVersion;
    const sessionTerminated = responseData.sessionTerminated || false;
    const terminatedCount = responseData.terminatedCount || 0;
    const consentProvided = responseData.consentProvided || false;
    const otpVerified = responseData.otpVerified || false;
    
    if (sessionTerminated) {
      console.log(`📌 ${terminatedCount} previous session(s) terminated with consent: ${consentProvided}`);
    }
    
    if (otpVerified) {
      console.log('✅ OTP verified for this session');
    }
    
    if (!userIdFromResponse) {
      console.error('❌ No user ID in response');
      throw new Error('Unable to identify user. Please contact support.');
    }
    if (!token) {
      console.error('❌ No token in response');
      throw new Error('Authentication failed. Please try again.');
    }
    if (!role) {
      console.error('❌ No role in response');
      throw new Error('Unable to determine user role. Please contact support.');
    }
    
    console.log('✅ User authenticated:', { userId: userIdFromResponse, role, otpVerified });
    
    if (responseData.subscription) {
      const subscription = responseData.subscription;
      subscription.isTenantAdmin = subscription.isTenantAdmin || 
                                 role === 'tenant_admin' || 
                                 role === 'super_admin';
      
      localStorage.setItem('subscription', JSON.stringify(subscription));
      console.log('✅ Subscription stored:', {
        hasActivePlan: subscription.hasActivePlan,
        status: subscription.status,
        planName: subscription.planName,
        isTenantAdmin: subscription.isTenantAdmin
      });
    } else {
      const defaultSubscription = {
        hasActivePlan: false,
        isActive: false,
        status: 'none',
        planName: 'No Plan',
        planId: null,
        daysRemaining: 0,
        isExpiringSoon: false,
        endDate: null,
        isTenantAdmin: role === 'tenant_admin' || role === 'super_admin'
      };
      localStorage.setItem('subscription', JSON.stringify(defaultSubscription));
    }
    
    if (responseData.evaluatorInfo) {
      localStorage.setItem('evaluatorInfo', JSON.stringify(responseData.evaluatorInfo));
    }
    
    if (tokenVersion !== undefined) {
      localStorage.setItem('tokenVersion', tokenVersion.toString());
    }
    
    if (sessionId) {
      localStorage.setItem('sessionId', sessionId);
    }
    
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userIdFromResponse);
    localStorage.setItem('userRole', role);
    if (tenantIdFromResponse) {
      localStorage.setItem('tenantId', tenantIdFromResponse);
    }
    if (mustChangePassword) {
      localStorage.setItem('mustChangePassword', 'true');
      localStorage.setItem('passwordChangeReason', passwordChangeReason);
    } else {
      localStorage.removeItem('mustChangePassword');
      localStorage.removeItem('passwordChangeReason');
      localStorage.removeItem('postPasswordChangeRoute');
    }
    
    setLoginAttempts(prev => prev + 1);
    
    window.dispatchEvent(new CustomEvent('subscriptionUpdated', { 
      detail: { subscription: JSON.parse(localStorage.getItem('subscription') || '{}') }
    }));
    
    await handleRoleRedirect(role, userIdFromResponse, tenantIdFromResponse, mustChangePassword, responseData);
  };

  const handleRoleRedirect = async (role, userId, tenantId, mustChangePassword, responseData) => {
    const currentSubdomain = window.location.hostname.split('.')[0];
    
    if (role === 'evaluator') {
      console.log('👨‍🏫 Evaluator user - redirecting to evaluator dashboard');
      if (mustChangePassword) {
        localStorage.setItem('postPasswordChangeRoute', '/evaluator/dashboard');
        navigate('/change-password', {
          replace: true,
          state: {
            nextRoute: '/evaluator/dashboard',
            userId: userId,
            tenantId: tenantId,
            role: role
          }
        });
        return;
      }
      navigate('/evaluator/dashboard', {
        state: {
          userId: userId,
          tenantId: tenantId,
          evaluatorInfo: responseData.evaluatorInfo
        }
      });
      return;
    }
    
    if (role === 'faculty') {
      console.log('🎓 Faculty user - redirecting to faculty dashboard');
      if (mustChangePassword) {
        localStorage.setItem('postPasswordChangeRoute', '/faculty/dashboard');
        navigate('/change-password', {
          replace: true,
          state: {
            nextRoute: '/faculty/dashboard',
            userId: userId,
            tenantId: tenantId,
            role: role
          }
        });
        return;
      }
      navigate('/faculty/dashboard', {
        state: {
          userId: userId,
          tenantId: tenantId
        }
      });
      return;
    }
    
    if (role === 'coe' || role === 'coe_coordinator') {
      console.log('👔 COE user - redirecting to COE dashboard');
      if (mustChangePassword) {
        localStorage.setItem('postPasswordChangeRoute', '/coe');
        navigate('/change-password', {
          replace: true,
          state: {
            nextRoute: '/coe',
            userId: userId,
            tenantId: tenantId,
            role: role
          }
        });
        return;
      }
      navigate('/coe', {
        state: {
          userId: userId,
          tenantId: tenantId
        }
      });
      return;
    }
    
    console.log('2️⃣ Step 2: Checking access permissions...');
    const accessCheckResponse = await api.post(`/api/access/check-access`, {
      userId: userId,
      subdomain: currentSubdomain,
    });
    
    const { route, user } = accessCheckResponse.data;
    console.log('✅ Access granted - Route:', route);
    
    if (route) {
      setUserId(userId);
      setUserRole(role);
      if (user?.tenantId) setTenantId(user.tenantId);

      if (mustChangePassword) {
        localStorage.setItem('postPasswordChangeRoute', route);
        navigate('/change-password', {
          replace: true,
          state: {
            nextRoute: route,
            userId: userId,
            tenantId: user?.tenantId || tenantId,
            role: role
          }
        });
        return;
      }
      
      navigate(route, {
        replace: true,
        state: {
          userId: userId,
          tenantId: user?.tenantId || tenantId,
          role: role,
          mustChangePassword: mustChangePassword,
          fromLogin: true
        }
      });
    } else {
      throw new Error('No access route determined');
    }
  };

  const performLogin = async (currentSubdomain, forceLogin = false, deviceId = null) => {
    // ✅ Get device ID if not provided
    const finalDeviceId = deviceId || getDeviceId();
    
    const loginData = {
      email: email.trim().toLowerCase(),
      password,
      subdomain: currentSubdomain,
      forceLogin: forceLogin,
      deviceId: finalDeviceId // ✅ Always include device ID
    };
    
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          });
        });
        
        loginData.latitude = position.coords.latitude;
        loginData.longitude = position.coords.longitude;
        console.log('✅ Location captured');
      } catch (geoError) {
        console.log('⚠️ Location not available:', geoError.message);
      }
    }
    
    console.log('2️⃣ Step 2: Authenticating user...');
    console.log('📱 Device ID:', finalDeviceId);
    const response = await api.post(`/api/new-auth/subdomain-signin`, loginData);
    
    const responseData = response.data;
    console.log('✅ Authentication response received');
    
    if (responseData.requiresOTP) {
      console.log('🔐 OTP required for login:', responseData.reason);
      
      setOtpLoginData(loginData);
      setOtpEmail(loginData.email);
      setOtpError(null);
      setOtpRemainingAttempts(3);
      setOtpTimerSeconds(60);
      
      setShowOTPModal(true);
      setButtonLoading(false);
      loginInProgressRef.current = false;
      
      return;
    }
    
    await handleLoginSuccess(responseData);
  };

  const handleSignIn = async (e, deviceId) => {
    e.preventDefault();
    
    if (loginInProgressRef.current) {
      console.log('⏳ Login already in progress, skipping...');
      return;
    }
    
    loginInProgressRef.current = true;
    setButtonLoading(true);
    setLoginError(null);
    
    console.group('🔐 Sign In Process');
    console.log('🔐 Attempting sign in for:', email);
    
    // ✅ Get device ID if not provided
    const finalDeviceId = deviceId || getDeviceId();
    console.log('📱 Device ID:', finalDeviceId);
    
    try {
      const currentSubdomain = window.location.hostname.split('.')[0];
      console.log('🌐 Detected subdomain:', currentSubdomain);
      
      // ✅ Ensure device ID is stored
      if (finalDeviceId) {
        localStorage.setItem('deviceId', finalDeviceId);
      }
      
      // Step 1: Check for active sessions
      console.log('1️⃣ Step 1: Checking for active sessions...');
      
      const sessionCheckResponse = await api.post('/api/new-auth/check-sessions', {
        email: email.trim().toLowerCase()
      });
      
      const sessionData = sessionCheckResponse.data;
      
      if (sessionData.hasActiveSessions) {
        console.log(`🔴 Found ${sessionData.activeSessionCount} active session(s). Showing consent modal.`);
        setPendingSessions(sessionData.sessions);
        setShowConsentModal(true);
        setButtonLoading(false);
        loginInProgressRef.current = false;
        console.groupEnd();
        return;
      }
      
      console.log('✅ No active sessions found. Proceeding with login.');
      
      // ✅ Pass deviceId to performLogin
      await performLogin(currentSubdomain, false, finalDeviceId);
      
    } catch (err) {
      console.error('❌ Login error:', err);
      
      if (err.response?.data?.requiresOTP) {
        console.log('🔐 OTP required from error response');
        setOtpLoginData({
          email: email.trim().toLowerCase(),
          password,
          subdomain: window.location.hostname.split('.')[0],
          deviceId: finalDeviceId
        });
        setOtpEmail(email.trim().toLowerCase());
        setOtpError(null);
        setOtpRemainingAttempts(3);
        setOtpTimerSeconds(60);
        setShowOTPModal(true);
        setButtonLoading(false);
        loginInProgressRef.current = false;
        console.groupEnd();
        return;
      }
      
      handleLoginError(err);
    } finally {
      if (!showOTPModal && !showConsentModal) {
        loginInProgressRef.current = false;
        setButtonLoading(false);
      }
      console.groupEnd();
    }
  };

  const handleConsentConfirm = async () => {
    setConsentLoading(true);
    setLoginError(null);
    
    try {
      const currentSubdomain = window.location.hostname.split('.')[0];
      console.log('✅ User provided consent. Proceeding with login with forceLogin=true');
      
      // ✅ Get device ID for consent login
      const deviceId = getDeviceId();
      await performLogin(currentSubdomain, true, deviceId);
      
    } catch (err) {
      console.error('❌ Login after consent error:', err);
      handleLoginError(err);
    } finally {
      setConsentLoading(false);
      setShowConsentModal(false);
      if (!showOTPModal) {
        loginInProgressRef.current = false;
        setButtonLoading(false);
      }
    }
  };

  const handleConsentCancel = () => {
    console.log('❌ User cancelled login due to active sessions.');
    setShowConsentModal(false);
    setPendingSessions([]);
    loginInProgressRef.current = false;
    setButtonLoading(false);
    setLoginError('Login cancelled. You can continue using your existing session.');
  };

  const handleLoginError = (err) => {
    if (err.response?.data?.code === 'ACTIVE_SESSIONS_EXIST') {
      const data = err.response.data;
      setPendingSessions(data.sessions || []);
      setShowConsentModal(true);
      return;
    }
    
    if (err.response?.data?.code === 'TOKEN_VERSION_MISMATCH') {
      setLoginError('Your session has expired because you logged in from another device. Please login again.');
      localStorage.clear();
      loginInProgressRef.current = false;
      setButtonLoading(false);
      return;
    }
    
    if (err.response?.data?.code === 'LEGACY_TOKEN') {
      setLoginError('Please login again to continue with the latest security update.');
      localStorage.clear();
      loginInProgressRef.current = false;
      setButtonLoading(false);
      return;
    }
    
    if (err.response?.data?.code === 'SESSION_INACTIVE') {
      setLoginError('Your session has expired or was terminated. Please login again.');
      localStorage.clear();
      loginInProgressRef.current = false;
      setButtonLoading(false);
      return;
    }
    
    if (err.message.includes('No user ID') || err.message.includes('No role')) {
      setLoginError('System configuration error. Please contact support.');
    } else if (err.response?.status === 401) {
      setLoginError('Invalid email or password. Please try again.');
    } else if (err.response?.status === 403) {
      const errorCode = err.response.data?.code;
      if (errorCode === 'LOCATION_REQUIRED') {
        setLoginError('Location access is required for evaluator login. Please enable location services and try again.');
      } else if (errorCode === 'GEOFENCE_VIOLATION') {
        const distance = err.response.data?.details?.distance;
        setLoginError(`Access denied. You must be within the permitted area to log in. ${distance ? `Current distance: ${Math.round(distance)} meters.` : ''} Please contact COE office.`);
      } else if (errorCode === 'ACCESS_TIME_VIOLATION') {
        setLoginError(err.response.data?.message || 'Your access period has not started or has expired.');
      } else if (errorCode === 'DAILY_LIMIT_EXCEEDED') {
        setLoginError(err.response.data?.message || 'You have reached your daily login limit.');
      } else if (errorCode === 'ROLE_NOT_ALLOWED') {
        setLoginError(err.response.data?.message || 'Your role is not included in the current subscription plan.');
      } else if (errorCode === 'SUBSCRIPTION_INACTIVE') {
        setLoginError(err.response.data?.message || 'Your institution\'s subscription is not active. Please contact your administrator.');
      } else if (errorCode === 'SUBSCRIPTION_EXPIRED') {
        setLoginError(err.response.data?.message || 'Your institution\'s subscription has expired. Please contact your administrator.');
      } else {
        setLoginError('Access denied. You do not have permission to access this institution.');
      }
    } else if (err.response?.status === 404) {
      setLoginError('User account not found. Please check your email or contact support.');
    } else if (err.response?.status === 429) {
      setLoginError('Too many login attempts. Please try again later.');
    } else if (err.code === 'ERR_NETWORK') {
      setLoginError('Network error. Please check your internet connection.');
    } else {
      setLoginError(err.response?.data?.message || err.message || 'Login failed. Please try again.');
    }
    
    loginInProgressRef.current = false;
    setButtonLoading(false);
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '24px',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          width: '100%',
          maxWidth: '220px', 
          height: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Lottie
            animationData={bookLoadingAnimation}
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <h3 style={{ 
            fontSize: '17px', 
            fontWeight: '600', 
            color: '#0f172a', 
            margin: '0 0 6px 0',
            letterSpacing: '-0.01em'
          }}>
            Loading Portal Environment
          </h3>
          <p style={{ 
            color: '#64748b', 
            fontSize: '14px', 
            lineHeight: '1.5',
            margin: 0,
            maxWidth: '280px'
          }}>
            Connecting securely to your institution configuration workspace...
          </p>
        </div>
      </div>
    );
  }
  
  if (status === 'disabled') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '40px 32px',
          maxWidth: '460px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
            fontSize: '28px',
            color: '#ef4444'
          }}>
            🚫
          </div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#0f172a',
            letterSpacing: '-0.02em'
          }}>
            Subdomain Disabled
          </h1>
          <p style={{
            color: '#64748b',
            lineHeight: '1.6',
            fontSize: '14px',
            margin: 0
          }}>
            This operational subdomain has been temporarily suspended by the structural administrator. Please check coordinates later or reach system infrastructure directly.
          </p>
        </div>
      </div>
    );
  }
  
  if (status === 'not_found') {
    return <DisabledPage />;
  }
  
  if (status === 'error') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '45px 35px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fffbeb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
            fontSize: '26px',
            color: '#f59e0b'
          }}>
            ⚠️
          </div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#0f172a',
            letterSpacing: '-0.02em'
          }}>
            System Context Error
          </h1>
          <p style={{
            color: '#64748b',
            lineHeight: '1.6',
            fontSize: '14px',
            marginBottom: '28px'
          }}>
            {error || 'An unexpected operational breakdown occurred trying to process this segment context.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#0f172a',
              color: '#ffffff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              width: '100%'
            }}
            onMouseEnter={(e) => e.target.style.background = '#1e293b'}
            onMouseLeave={(e) => e.target.style.background = '#0f172a'}
          >
            Reinitialize Interface
          </button>
        </div>
      </div>
    );
  }
  
  if (status === 'active') {
    return (
      <>
        {websiteData ? (
          <TenantWebsite
            websiteData={websiteData}
            subdomain={subdomain}
            handleSignIn={handleSignIn}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            loginError={loginError}
            buttonLoading={buttonLoading}
            onTenantBrandingUpdate={onTenantBrandingUpdate}
          />
        ) : (
          <DefaultTemplate
            subdomain={subdomain}
            handleSignIn={handleSignIn}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            loginError={loginError}
            buttonLoading={buttonLoading}
          />
        )}
        
        {showConsentModal && (
          <SessionConsentModal
            sessions={pendingSessions}
            onConfirm={handleConsentConfirm}
            onCancel={handleConsentCancel}
            loading={consentLoading}
          />
        )}
        
        {showOTPModal && (
          <OTPModal
            isOpen={showOTPModal}
            onClose={() => {
              setShowOTPModal(false);
              setButtonLoading(false);
              loginInProgressRef.current = false;
            }}
            onVerify={handleOTPVerify}
            onResend={handleOTPResend}
            email={otpEmail}
            isLoading={otpLoading}
            error={otpError}
            remainingAttempts={otpRemainingAttempts}
            timerSeconds={otpTimerSeconds}
            onTimerComplete={handleOTPTimerComplete}
          />
        )}
      </>
    );
  }
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '36px',
        textAlign: 'center',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
        border: '1px solid #e2e8f0',
        maxWidth: '400px',
        width: '100%'
      }}>
        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 20px 0' }}>An unexpected error occurred. Please try again later.</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#0f172a',
            color: '#ffffff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default Dashboard;